import { useCallback, useEffect, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import Loading from "../../../components/Loading";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { getGridcoDemandMetrics, getGridcoScheduleDemandMetrics } from "../../../Rest_api/RestApi";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { H_CHART_TYPES } from "../../Hppc/const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import { getTimeRange } from "../../Dashboard/Exchange3/Chart";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import { MetricChipHead } from "../../../components/Metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { DiscomGridcoData } from "../../../models/metrics";
const BLOCKS_96 = Array.from({ length: 96 }, (_, i) => i + 1);

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" }
];

const UNITS = ["MW", "MWh", "LU"];

const TYPE = ["Drawal", "Schedule"];

interface GridcoSummaryAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  metrics: DiscomGridcoData | null;
}

export function GridcoSummaryAnalysis({ 
  onXAxisModeChange, 
  selectedUnit,
  onUnitChange,
  metrics 
}: GridcoSummaryAnalysisProps) {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );

  const [drawalData, setDrawalData] = useState<any[]>([]);
  const [scheduleData, setScheduleData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedType, setSelectedType] = useState<string[]>([TYPE[0]]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSummaryData = useCallback(async () => {
    if (DefaultDates[activeTab]?.defaultRangeDate === undefined) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    
    try {
      const dates = DefaultDates[activeTab].defaultRangeDate;
      const startDate = dates.startDate.toISOString().split("T")[0];
      const endDate = dates.endDate.toISOString().split("T")[0];

      // Always fetch both drawal and schedule data in parallel
      const [drawalMetrics, scheduleMetrics] = await Promise.all([
        getGridcoDemandMetrics(startDate, endDate),
        getGridcoScheduleDemandMetrics(startDate, endDate)
      ]);

      setDrawalData(drawalMetrics);
      setScheduleData(scheduleMetrics);
    } catch (error) {
      console.error("Error fetching summary data:", error);
      setDrawalData([]);
      setScheduleData([]);
    }
    
    setLoading(false);
  }, [DefaultDates, activeTab]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  useEffect(() => {
    const isDrawalSelected = selectedType.includes("Drawal");
    const isScheduleSelected = selectedType.includes("Schedule");

    let combinedData: any[] = [];

    if (isDrawalSelected && isScheduleSelected) {
      // Merge both datasets
      combinedData = mergeDrawalAndScheduleData(drawalData, scheduleData);
    } else if (isDrawalSelected) {
      combinedData = drawalData;
    } else if (isScheduleSelected) {
      // Rename schedule fields to have schedule_ prefix for consistency
      combinedData = scheduleData.map(item => ({
        ...item,
        schedule_min_demand: item.min_demand,
        schedule_max_demand: item.max_demand,
        schedule_avg_demand: item.avg_demand,
        schedule_mode_demand_window_formula: item.mode_demand_window_formula,
        schedule_date_of_min: item.date_of_min,
        schedule_date_of_max: item.date_of_max,
      }));
    }

    const processedData = combinedData.map((item) => {
      const conversionFactor = selectedUnit === "MWh" ? 0.25 : selectedUnit === "LU" ? 0.0025 : 1;
      
      const converted: any = {
        slot: item.slot,
        slot_number: item.slot,
        slot_with_time: item.slot && item.time_string ? `${item.slot} (${item.time_string})` : item.slot,
      };

      // Convert drawal fields if present
      if (item.min_demand !== undefined) {
        converted.min_demand = item.min_demand * conversionFactor;
        converted.date_of_min = item.date_of_min;
      }
      if (item.max_demand !== undefined) {
        converted.max_demand = item.max_demand * conversionFactor;
        converted.date_of_max = item.date_of_max;
      }
      if (item.avg_demand !== undefined) {
        converted.avg_demand = item.avg_demand * conversionFactor;
      }
      if (item.mode_demand_window_formula !== undefined) {
        converted.mode_demand_window_formula = item.mode_demand_window_formula * conversionFactor;
      }

      // Convert schedule fields if present
      if (item.schedule_min_demand !== undefined) {
        converted.schedule_min_demand = item.schedule_min_demand * conversionFactor;
        converted.schedule_date_of_min = item.schedule_date_of_min;
      }
      if (item.schedule_max_demand !== undefined) {
        converted.schedule_max_demand = item.schedule_max_demand * conversionFactor;
        converted.schedule_date_of_max = item.schedule_date_of_max;
      }
      if (item.schedule_avg_demand !== undefined) {
        converted.schedule_avg_demand = item.schedule_avg_demand * conversionFactor;
      }
      if (item.schedule_mode_demand_window_formula !== undefined) {
        converted.schedule_mode_demand_window_formula = item.schedule_mode_demand_window_formula * conversionFactor;
      }

      return converted;
    });

    const filteredData = selectedBlock.length > 0
      ? processedData.filter(item => selectedBlock.map(b => b.value).includes(item.slot))
      : processedData;

    setChartData(filteredData);
    setTableData(filteredData);
  }, [drawalData, scheduleData, selectedUnit, selectedType, selectedBlock]); 

  function mergeDrawalAndScheduleData(drawal: any[], schedule: any[]) {
    const mergedMap = new Map();

    // Add all drawal data
    drawal.forEach(item => {
      mergedMap.set(item.slot, { ...item });
    });

    // Merge schedule data
    schedule.forEach(item => {
      const existing = mergedMap.get(item.slot);
      
      if (existing) {
        mergedMap.set(item.slot, {
          ...existing,
          schedule_min_demand: item.min_demand,
          schedule_max_demand: item.max_demand,
          schedule_avg_demand: item.avg_demand,
          schedule_mode_demand_window_formula: item.mode_demand_window_formula,
          schedule_date_of_min: item.date_of_min,
          schedule_date_of_max: item.date_of_max,
        });
      } else {
        mergedMap.set(item.slot, {
          ...item,
          schedule_min_demand: item.min_demand,
          schedule_max_demand: item.max_demand,
          schedule_avg_demand: item.avg_demand,
          schedule_mode_demand_window_formula: item.mode_demand_window_formula,
          schedule_date_of_min: item.date_of_min,
          schedule_date_of_max: item.date_of_max,
        });
      }
    });

    return Array.from(mergedMap.values());
  }

  const getLegends = () => {
    const legends: any[] = [];
    const isDrawalSelected = selectedType.includes("Drawal");
    const isScheduleSelected = selectedType.includes("Schedule");

    if (isDrawalSelected) {
      legends.push(
        {
          dataKey: "min_demand",
          name: "Min Demand (Drawal)",
          // stroke: ExchangeColors[0],
          type: selectedChartType.value,
          stackId: selectedChartType.stackId !== undefined ? "summary" : undefined,
        },
        {
          dataKey: "max_demand", 
          name: "Max Demand (Drawal)",
          // stroke: ExchangeColors[1],
          type: selectedChartType.value,
          stackId: selectedChartType.stackId !== undefined ? "summary" : undefined,
        },
        {
          dataKey: "avg_demand",
          name: "Average Demand (Drawal)", 
          // stroke: ExchangeColors[2],
          type: selectedChartType.value,
          stackId: selectedChartType.stackId !== undefined ? "summary" : undefined,
        },
        {
          dataKey: "mode_demand_window_formula",
          name: "Mode Demand (Drawal)", 
          // stroke: ExchangeColors[3],
          type: selectedChartType.value,
          stackId: selectedChartType.stackId !== undefined ? "summary" : undefined,
        }
      );
    }

    if (isScheduleSelected) {
      legends.push(
        {
          dataKey: "schedule_min_demand",
          name: "Min Demand (Schedule)",
          // stroke: ExchangeColors[4],
          type: selectedChartType.value,
          stackId: selectedChartType.stackId !== undefined ? "summary" : undefined,
        },
        {
          dataKey: "schedule_max_demand", 
          name: "Max Demand (Schedule)",
          // stroke: ExchangeColors[5],
          type: selectedChartType.value,
          stackId: selectedChartType.stackId !== undefined ? "summary" : undefined,
        },
        {
          dataKey: "schedule_avg_demand",
          name: "Average Demand (Schedule)", 
          // stroke: ExchangeColors[6],
          type: selectedChartType.value,
          stackId: selectedChartType.stackId !== undefined ? "summary" : undefined,
        },
        {
          dataKey: "schedule_mode_demand_window_formula",
          name: "Mode Demand (Schedule)", 
          // stroke: ExchangeColors[7],
          type: selectedChartType.value,
          stackId: selectedChartType.stackId !== undefined ? "summary" : undefined,
        }
      );
    }

    return legends;
  };

  function handleTypeChange(item: string, index: number) {
    const selectedItem = TYPE[index];
    
    if (selectedType.includes(selectedItem)) {
      // Don't allow deselecting if it's the only selected item
      if (selectedType.length === 1) {
        return;
      }
      setSelectedType(selectedType.filter(t => t !== selectedItem));
    } else {
      setSelectedType([...selectedType, selectedItem]);
    }
  }

  const customTooltipFormatter = useCallback((params: any) => {
    const param = Array.isArray(params) ? params[0] : params;
    let items = Array.isArray(params) ? params : [params];
    
    let title = param.name;
    const isTimeSlot = true;
    if (isTimeSlot) {
      title = getTimeRange(parseInt(param.name)) + ` (${param.name})`;
    }

    const dataPoint = chartData[param.dataIndex];
    
    const sortedItems = [...items].sort((a, b) => {
      const nameA = (a.seriesName || "").toLowerCase();
      const nameB = (b.seriesName || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });

    const tooltipItems = sortedItems.map((item) => {
      const value = parseFloat(item.value)
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      const bgColor = item.color;
      
      const legends = getLegends();
      const legend = legends.find((l: any) => l.name === item.seriesName);
      
      let suffix = "";
      const suffixDict = {
        Price: "Rs/MWh",
        Percent: "%",
        "%": "%",
        Cost: "Rs",
        Revenue: "Rs",
      };

      const foundKey = Object.keys(suffixDict).find((key) =>
        item.seriesName?.toString().includes(key)
      );

      suffix = foundKey
        ? suffixDict[foundKey as keyof typeof suffixDict]
        : selectedUnit || "";

      let displaySeriesName = item.seriesName;
      
      // Handle drawal dates
      if (item.seriesName === "Min Demand (Drawal)" && dataPoint?.date_of_min) {
        displaySeriesName = `Min Demand (Drawal) (${dataPoint.date_of_min})`;
      } else if (item.seriesName === "Max Demand (Drawal)" && dataPoint?.date_of_max) {
        displaySeriesName = `Max Demand (Drawal) (${dataPoint.date_of_max})`;
      }
      // Handle schedule dates
      else if (item.seriesName === "Min Demand (Schedule)" && dataPoint?.schedule_date_of_min) {
        displaySeriesName = `Min Demand (Schedule) (${dataPoint.schedule_date_of_min})`;
      } else if (item.seriesName === "Max Demand (Schedule)" && dataPoint?.schedule_date_of_max) {
        displaySeriesName = `Max Demand (Schedule) (${dataPoint.schedule_date_of_max})`;
      }

      return `
        <div class="tooltip-item" data-name="${item.seriesName?.toLowerCase()}">
          <div class="color-indicator" style="background-color: ${bgColor}"></div>
          <div class="series-name">${displaySeriesName}</div>
          <div class="value">${value} ${suffix}</div>
        </div>
      `;
    });

    const tooltipId = `tooltip-${Date.now()}`;

    return `
      <div class="enhanced-tooltip" id="${tooltipId}">
        <div class="tooltip-header">
          <div class="tooltip-title">${title}</div>
        </div>
        
        <div class="tooltip-content">
          ${tooltipItems.join("")}
        </div>
      </div>
      
      <style>
        .enhanced-tooltip {
          font-family: 'Inter', system-ui, sans-serif;
          width: 100%;
          min-width: 200px;
        }
        .tooltip-header {
          display: flex;
          flex-direction: column;
          gap: 8px;
          justify-content: start;
          align-items: start;
          border-bottom: 1px solid #eaeaea;
          padding-bottom: 8px;
          margin-bottom: 8px;
        }
        .tooltip-title {
          font-weight: 600;
          font-size: 13px;
          color: #333;
        }
        .tooltip-content {
          max-height: 300px;
          overflow-y: auto;
          padding-right: 10px;
        }
        .tooltip-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 4px 0;
          border-bottom: 1px solid #f0f0f0;
        }
        .tooltip-item:last-child {
          border-bottom: none;
        }                    
        .color-indicator {
          width: 10px;
          height: 10px;
          flex-shrink: 0;
          border-radius: 50%;
        }
        .series-name {
          font-weight: 500;
          font-size: 12px;
          color: #333;
          flex: 1;
        }
        .value {
          font-weight: 600;
          font-size: 12px;
          color: #333;
        }
      </style>
    `;
  }, [chartData, selectedUnit]);

  // Build extra keys and labels dynamically based on selected type
  const getExtraKeysAndLabels = () => {
    const isDrawalSelected = selectedType.includes("Drawal");
    const isScheduleSelected = selectedType.includes("Schedule");

    const keys = ["slot_number"];
    const labels = ["Block"];

    if (isDrawalSelected) {
      keys.push("date_of_min", "date_of_max");
      labels.push("Min Date (Drawal)", "Max Date (Drawal)");
    }

    if (isScheduleSelected) {
      keys.push("schedule_date_of_min", "schedule_date_of_max");
      labels.push("Min Date (Schedule)", "Max Date (Schedule)");
    }

    return { keys, labels };
  };

  const { keys: extraKeys, labels: extraLabels } = getExtraKeysAndLabels();
  const { visibleLegends, handleLegendSelect, selectedLegends } =
        useLegendFilter(getLegends());
  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: tableData,
    defaultSortKey: "block",
    itemsPerPage: 96,
    unit: selectedUnit, 
    block_key: "slot_number",
    extraKeys: extraKeys,
    extraLabels: extraLabels,
    filename: "Gridco Load Summary",
  };

  return (
    <div className="gas-page-container">
      {loading && <Loading />}

      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Summary"]}
            onSelect={onXAxisModeChange}
          />

          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              onUnitChange(item);
            }}
          />

          <FilterMenu
            title="Type"
            items={TYPE}
            selectedItems={selectedType}
            onSelect={handleTypeChange}
          />

          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(H_CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
           <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChipHead
              icon={<PeakIcon width={20} height={19} />}
              title="Peak Load"
              items={[
                {
                  label: "Drawal",
                  value: metrics.drawal["Peak Load (MW)"].value,
                  unit: metrics.drawal["Peak Load (MW)"].unit,
                },
                {
                  label: "Schedule",
                  value: metrics.schedule["Peak Load (MW)"].value,
                  unit: metrics.schedule["Peak Load (MW)"].unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<AvgIcon width={20} height={19} />}
              title="Average Load"
              items={[
                {
                  label: "Drawal",
                  value: metrics.drawal["Average Load (MW)"].value,
                  unit: metrics.drawal["Average Load (MW)"].unit,
                },
                {
                  label: "Schedule",
                  value: metrics.schedule["Average Load (MW)"].value,
                  unit: metrics.schedule["Average Load (MW)"].unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<MinIcon width={20} height={19} />}
              title="Minimum Load"
              items={[
                {
                  label: "Drawal",
                  value: metrics.drawal["Minimum Load (MW)"].value,
                  unit: metrics.drawal["Minimum Load (MW)"].unit,
                },
                {
                  label: "Schedule",
                  value: metrics.schedule["Minimum Load (MW)"].value,
                  unit: metrics.schedule["Minimum Load (MW)"].unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<TotalIcon width={20} height={19} />}
              title="Total Energy"
              items={[
                {
                  label: "Drawal",
                  value: metrics.drawal["Total Energy (MU)"].value,
                  unit: metrics.drawal["Total Energy (MU)"].unit,
                },
                {
                  label: "Schedule",
                  value: metrics.schedule["Total Energy (MU)"].value,
                  unit: metrics.schedule["Total Energy (MU)"].unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-80">
              Discom Load Summary ({selectedUnit})
            </h2>
            <div className="flex gap-x-6 justify-end">
              <BlockRangeSelector
                blocks={BLOCKS_96}
                placeholder="Search Block"
                selectedOptions={selectedBlock.map((item) => item.value)}
                setSelectedOptions={setSelectedBlock}
              />
              <button
                onClick={() => {
                  dataTab.label === "Chart" && downloadChartTableCSV(tablePar);
                  dataTab.label === "Summary" && downloadSummaryCSV(tablePar);
                  dataTab.label === "Table" && downloadChartTableCSV(tablePar);
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>

          {dataTab.label === "Chart" && (
            <ReMixChart
              key={`${selectedChartType.value}-${selectedUnit}`}
              data={chartData}
              showBrush={true}
              isTimeSlot={false}
              onlyTitle={true}
              barCategoryGap={10}
              unit={selectedUnit}
              yAxisLabel={selectedUnit}
              legends={getLegends()}
              xDataKey="slot"
              secondXDataKey={undefined}
              customTooltip={customTooltipFormatter}
              onLegendSelect={handleLegendSelect}
              defaultSelectedLegends={selectedLegends}
            />
          )}

          {dataTab.label === "Summary" && <LegendSummaryTable {...tablePar} />}
          {dataTab.label === "Table" && <ChartTable {...tablePar} />}
        </div>

        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5">
            <Tooltip
              note={<p>Gridco : BSP bills & ABT Meters</p>}
              text="1. BSP Bills : Annexure 1 is used for cumulation.
              2. Some meter numbers in Annexure 1 were missing in provided ABT Meter files."
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
